import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';

import { updateUserDefaultBundleMutation } from '../update-user-default-bundle.operations';

import { getTenantUsersForBulkBundleQuery } from './bulk-default-bundle.operations';

export type BulkTargetUser = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
};

export type BulkRowResult = {
  user: BulkTargetUser;
  ok: boolean;
  error?: string;
};

export type BulkRunStatus = 'idle' | 'running' | 'done';

/**
 * Maximum number of in-flight per-user mutations. Keeps Hasura headroom while
 * still draining a 17-doctor cohort in well under a second.
 */
const CONCURRENCY = 5;

const runWithConcurrency = async <T>(
  tasks: Array<() => Promise<T>>,
  limit: number,
): Promise<T[]> => {
  const results = new Array<T>(tasks.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () =>
    (async () => {
      while (true) {
        const index = cursor++;
        const task = tasks[index];
        if (!task) return;
        results[index] = await task();
      }
    })(),
  );
  await Promise.all(workers);
  return results;
};

/**
 * Runs the same per-PK `updateTenantUserSettingsByPk` mutation that the
 * single-user flow uses, once per affected user. Reusing the per-row mutation
 * (rather than a single bulk `_set` over a `where`) ensures the
 * `validate_user_settings_template_bundle` trigger and the
 * `tenant_user_settings_history` versioning trigger fire per row, and lets us
 * report partial success when validation rejects a specific user.
 */
export const useBulkUpdateDefaultBundle = (tenantId: string) => {
  const client = useApolloClient();
  const [status, setStatus] = useState<BulkRunStatus>('idle');
  const [results, setResults] = useState<BulkRowResult[]>([]);

  const reset = useCallback(() => {
    setStatus('idle');
    setResults([]);
  }, []);

  const run = useCallback(
    async (
      users: BulkTargetUser[],
      targetBundleId: string,
    ): Promise<BulkRowResult[]> => {
      setStatus('running');
      setResults([]);

      const tasks = users.map((user) => async (): Promise<BulkRowResult> => {
        try {
          const { data, errors } = await client.mutate({
            mutation: updateUserDefaultBundleMutation,
            variables: {
              tenantId,
              userId: user.userId,
              defaultTemplateBundleId: targetBundleId,
            },
            context: eluveAdminHasuraContext,
          });
          const firstError = errors?.[0];
          if (firstError) {
            return { user, ok: false, error: firstError.message };
          }
          if (!data?.updateTenantUserSettingsByPk) {
            return {
              user,
              ok: false,
              error:
                'No tenant_user_settings row exists for this user. They need to sign in once before their default bundle can be set.',
            };
          }
          return { user, ok: true };
        } catch (err) {
          return {
            user,
            ok: false,
            error: err instanceof Error ? err.message : String(err),
          };
        }
      });

      let settled: BulkRowResult[];
      try {
        settled = await runWithConcurrency(tasks, CONCURRENCY);
        setResults(settled);
      } finally {
        setStatus('done');
      }

      // Refetch the bulk query so any open dialog (and a re-opened one) reflect
      // the new state immediately — covers the AC that re-opening Migrate after
      // a successful run shows zero users for the previous source bundle.
      // Swallow refetch errors so a transient network failure here doesn't
      // reject `run()` and prevent callers from showing the result toast.
      try {
        await client.refetchQueries({
          include: [getTenantUsersForBulkBundleQuery],
        });
      } catch {
        // intentionally ignored — results are already in state
      }

      return settled;
    },
    [client, tenantId],
  );

  return { status, results, run, reset };
};
